import React, { useEffect, useState } from "react";

import { useForm } from "react-hook-form/dist/react-hook-form.ie11";
import { useHistory, useParams } from "react-router-dom";

import { fireOnce, objectKeys } from "@ds-developer/frontend";

import { getBrandRoute } from "@hubcapp/utils";
import { useAuth, useTealium } from "@hubcapp/context";

import {
  AccessCodeFormFields,
  AccessCodeProps,
  StateProviderProps,
} from "./types";

import AccessCode from "./AccessCode";
import validationSchema from "./validationSchema";

const AccessCodeStateProvider: React.FC<StateProviderProps> = ({
  initialFormFields,
}) => {
  const { push } = useHistory();
  const { brand } = useParams<{ brand: string }>();
  const { trackEvent } = useTealium();
  const {
    instructor: { adiNumber },
    confirmMFA,
    resendMFA,
  } = useAuth();

  const [loading, setLoading] = useState<boolean>(false);
  const [resendLoading, setResendLoading] = useState<boolean>(false);
  const [primaryErrorMessage, setPrimaryErrorMessage] = useState<string>("");

  const [resendStatus, setResendState] = useState<
    AccessCodeProps["resendStatus"]
  >(undefined);

  const {
    control,
    errors,
    formState: { isValid },
    handleSubmit,
    triggerValidation,
  } = useForm<AccessCodeFormFields>({
    defaultValues: initialFormFields,
    mode: "onChange",
    validationSchema,
  });

  if (!adiNumber) {
    push(`/${getBrandRoute(brand)}/sign-in`);
  }

  const handleResendClick = () => {
    trackEvent({
      eventCategory: "Access Code",
      eventAction: "Link Click",
      eventLabel: "Resend It",
    });
    setResendLoading(true);
    resendMFA()
      .then(() => {
        setResendLoading(false);
        setResendState("SUCCESS");
      })
      .catch(() => setResendState("FAILURE"));
  };

  const submit = ({ accessCode }: AccessCodeFormFields) => {
    trackEvent({
      eventCategory: "Access Code",
      eventAction: "Button Click",
      eventLabel: "Verify",
    });

    confirmMFA(accessCode)
      .then(() => push("/"))
      .catch(() => {
        setLoading(false);
        setPrimaryErrorMessage(
          "You have entered an incorrect access code. Please try again.",
        );
      });
  };

  const setInitialValidation = () => {
    if (initialFormFields) {
      triggerValidation(objectKeys(initialFormFields));
    }
  };

  const handleDigitCodeEvent = () => {
    fireOnce("Access Code|Interaction|Digit Code", () => {
      trackEvent({
        eventCategory: "Access Code",
        eventAction: "Interaction",
        eventLabel: "Digit Code",
      });
    });
  };

  useEffect(setInitialValidation, []);

  return (
    <AccessCode
      control={control}
      errors={errors}
      isLoading={loading}
      isValid={isValid}
      onResendClicked={handleResendClick}
      onSubmit={handleSubmit(submit)}
      handleDigitCodeEvent={handleDigitCodeEvent}
      primaryErrorMessage={primaryErrorMessage}
      resendLoading={resendLoading}
      resendStatus={resendStatus}
    />
  );
};

export default AccessCodeStateProvider;
